export type EventSource = 'client' | 'server' | 'job' | 'webhook';

export interface EventContext {
  user_id?: string;
  recipient_id?: string;
  collection_id?: string;
  conversation_id?: string;
  chapter_id?: string;
  contributor_id?: string;
  email_send_id?: string;
  agent_run_id?: string;
  request_id?: string;
  trace_id?: string;
  job_id?: string;
  provider_request_id?: string;
  /** True when the recipient is an internal/test address (see isInternalRecipient). */
  is_internal?: boolean;
  /** Canonical id of the upstream artifact (conversation_id, contribution_id, etc.) */
  source_id?: string;
  /** Discriminator naming the kind of artifact source_id refers to */
  source_type?: 'conversation' | 'inviter_briefing' | 'self_recording' | 'document';
  source: EventSource;
}

export function resolveDistinctId(ctx: EventContext): string {
  if (ctx.user_id) return ctx.user_id;
  if (ctx.recipient_id) return ctx.recipient_id;
  return 'anonymous';
}

/**
 * Merge the event-specific payload with the envelope's correlation IDs.
 * Identity fields (user_id, recipient_id) are intentionally excluded — they
 * resolve to the event's distinct_id, not a property on the event.
 * Precedence: `event_source` from ctx always wins over any same-key in props;
 * correlation IDs present in ctx overwrite same-key in props.
 */
export function mergeEnvelope<P extends Record<string, unknown>>(
  props: P,
  ctx: EventContext
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...props, event_source: ctx.source };
  if (ctx.collection_id) merged.collection_id = ctx.collection_id;
  if (ctx.conversation_id) merged.conversation_id = ctx.conversation_id;
  if (ctx.source_id) merged.source_id = ctx.source_id;
  if (ctx.source_type) merged.source_type = ctx.source_type;
  if (ctx.chapter_id) merged.chapter_id = ctx.chapter_id;
  if (ctx.contributor_id) merged.contributor_id = ctx.contributor_id;
  if (ctx.email_send_id) merged.email_send_id = ctx.email_send_id;
  if (ctx.agent_run_id) merged.agent_run_id = ctx.agent_run_id;
  if (ctx.request_id) merged.request_id = ctx.request_id;
  if (ctx.trace_id) merged.trace_id = ctx.trace_id;
  if (ctx.job_id) merged.job_id = ctx.job_id;
  if (ctx.provider_request_id) merged.provider_request_id = ctx.provider_request_id;
  if (ctx.is_internal !== undefined) merged.is_internal = ctx.is_internal;
  return merged;
}
